import {
  AccessTime,
  Assignment,
  ExpandLess,
  ExpandMore,
  PlayArrow,
  Stop,
} from "@mui/icons-material";
import {
  Avatar,
  AvatarGroup,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { format, parseISO, isAfter } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState } from "react";
import { useAuth } from "shared/hooks/auth";
import { ISubtarefaCard, ITarefaCard } from "services/modules/quadroTarefas";
import { segundosToHHMMSS } from "shared/helpers/horasHHMM";

interface TarefaCardProps {
  tarefa: ITarefaCard;
  onClickCard: (idTarefa: number) => void;
  // 3.1.2 — callbacks aceitam idSubtarefa opcional: quando informado,
  // cronometra a subtarefa; caso contrário, a tarefa.
  onPlayTimer?: (idTarefa?: number, idSubtarefa?: number) => void;
  onStopTimer?: (idTarefa?: number, idSubtarefa?: number) => void;
  timerAtivoIdTarefa?: number | null;
}

/**
 * Card de tarefa para o Kanban
 */
export const TarefaCard = ({
  tarefa,
  onClickCard,
  onPlayTimer,
  onStopTimer,
  timerAtivoIdTarefa,
}: TarefaCardProps) => {
  const { user, havePermission } = useAuth();
  const [subtarefasExpandidas, setSubtarefasExpandidas] = useState(false);

  // 8.1.3 — Gate de play/stop por responsável:
  // só pode cronometrar quem é responsável da tarefa/subtarefa OU tem a
  // permissão "Tarefa.EditarTerceiros".
  // FASE 04 item 2: alinhado à regra do backend — Master passa sempre e token
  // legado (sem o marcador "Tarefa.Esquema") libera; só sob o esquema é que a
  // sub-permissão específica é exigida.
  const podeEditarTerceiros =
    havePermission(["Master"]) ||
    !havePermission(["Tarefa.Esquema"]) ||
    havePermission(["Tarefa.EditarTerceiros"]);
  const ehResponsavel = (responsaveis: { idUsuario: number }[]): boolean =>
    user?.idUsuario != null &&
    responsaveis.some((r) => r.idUsuario === user.idUsuario);
  const podeCronometrarTarefa =
    podeEditarTerceiros || ehResponsavel(tarefa.responsaveis);
  const podeCronometrarSubtarefa = (sub: ISubtarefaCard): boolean =>
    podeEditarTerceiros || ehResponsavel(sub.responsaveis || []);

  const isAtrasada = tarefa.previsaoTermino
    ? isAfter(new Date(), parseISO(tarefa.previsaoTermino))
    : false;

  // Reteste 21/08 (item 6): o ícone segue o timer do PRÓPRIO usuário (timerAtivoIdTarefa);
  // temTimerAtivo do VM é agregado (subtarefas/outros usuários) e mostraria Stop errado.
  // O refresh após o play no modal vem do onSave() do handleToggleTimer.
  const isTimerAtivo = timerAtivoIdTarefa === tarefa.idTarefa;

  const subtarefas = tarefa.subtarefas || [];

  // Item 6.5 — exibir o tempo real em hh:mm:ss a partir de totalSegundosReal.
  // Fallback: totalMinutosReal*60 e, por fim, horasReal*3600.
  const formatTempoReal = (item: {
    totalSegundosReal?: number;
    totalMinutosReal?: number;
    horasReal?: number;
  }): string => {
    const segundos =
      item.totalSegundosReal != null
        ? item.totalSegundosReal
        : item.totalMinutosReal != null
        ? item.totalMinutosReal * 60
        : Math.round((item.horasReal || 0) * 3600);
    return segundosToHHMMSS(segundos);
  };

  const formatData = (data: string): string =>
    format(parseISO(data), "dd/MM/yy", { locale: ptBR });

  return (
    <Card
      sx={{
        mb: 1.5,
        cursor: "pointer",
        transition: "all 0.2s ease",
        border: isTimerAtivo ? "2px solid #4caf50" : "1px solid transparent",
        "&:hover": {
          boxShadow: 3,
          transform: "translateY(-2px)",
        },
      }}
      onClick={() => onClickCard(tarefa.idTarefa)}
    >
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        {/* Header: Número + Tipo de Atividade */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography variant="caption" color="primary" fontWeight="bold">
            #{tarefa.numero}
          </Typography>
          {tarefa.nomeTipoAtividade && (
            <Chip
              label={tarefa.nomeTipoAtividade}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.65rem" }}
            />
          )}
        </Box>

        {/* Título */}
        <Typography
          variant="body2"
          fontWeight="medium"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            mb: 1,
          }}
        >
          {tarefa.titulo}
        </Typography>

        {/* Referência */}
        <Typography variant="caption" color="textSecondary" display="block" mb={1}>
          {tarefa.nomeReferencia}
        </Typography>

        {/* Métricas: Horas + Sub-tarefas */}
        <Box display="flex" gap={1} mb={1} flexWrap="wrap">
          {/* Horas */}
          <Tooltip title={`Horas trabalhadas: ${formatTempoReal(tarefa)}`}>
            <Chip
              icon={<AccessTime sx={{ fontSize: 14 }} />}
              label={formatTempoReal(tarefa)}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: "0.7rem" }}
            />
          </Tooltip>

          {/* Sub-tarefas */}
          {tarefa.quantidadeSubtarefas > 0 && (
            <Tooltip
              title={`${tarefa.quantidadeSubtarefasConcluidas}/${tarefa.quantidadeSubtarefas} sub-tarefas concluídas`}
            >
              <Chip
                icon={<Assignment sx={{ fontSize: 14 }} />}
                label={`${tarefa.quantidadeSubtarefasConcluidas}/${tarefa.quantidadeSubtarefas}`}
                size="small"
                variant="outlined"
                color={
                  tarefa.quantidadeSubtarefasConcluidas === tarefa.quantidadeSubtarefas
                    ? "success"
                    : "default"
                }
                sx={{ height: 22, fontSize: "0.7rem" }}
              />
            </Tooltip>
          )}
        </Box>

        {/* Footer: Datas de Previsão + Responsáveis + Timer */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {/* Datas de Previsão (Início e Término) */}
          <Box display="flex" flexDirection="column">
            {tarefa.previsaoInicio && (
              <Tooltip title="Previsão de início">
                <Typography variant="caption" color="textSecondary">
                  Início: {formatData(tarefa.previsaoInicio)}
                </Typography>
              </Tooltip>
            )}
            {tarefa.previsaoTermino && (
              <Tooltip title="Previsão de término">
                <Typography
                  variant="caption"
                  color={isAtrasada ? "error" : "textSecondary"}
                  fontWeight={isAtrasada ? "bold" : "normal"}
                >
                  Término: {formatData(tarefa.previsaoTermino)}
                </Typography>
              </Tooltip>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={0.5} ml="auto">
            {/* 3.2 — Play/Stop DA TAREFA só quando NÃO há subtarefas
                (com subtarefas, o controle de tempo é por subtarefa — item 3.1).
                8.1.3 — escondido quando o usuário não é responsável da tarefa
                e não tem Tarefa.EditarTerceiros. */}
            {tarefa.quantidadeSubtarefas === 0 && podeCronometrarTarefa && (
              <>
            {isTimerAtivo ? (
              <Tooltip title="Parar timer">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStopTimer?.(tarefa.idTarefa);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <Stop fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Iniciar timer">
                <IconButton
                  size="small"
                  color="success"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlayTimer?.(tarefa.idTarefa);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <PlayArrow fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
              </>
            )}

            {/* Avatares dos Responsáveis */}
            {tarefa.responsaveis.length > 0 && (
              <AvatarGroup
                max={3}
                sx={{
                  "& .MuiAvatar-root": {
                    width: 24,
                    height: 24,
                    fontSize: "0.7rem",
                    border: "2px solid white",
                  },
                }}
              >
                {tarefa.responsaveis.map((resp) => (
                  <Tooltip key={resp.idUsuario} title={resp.nome}>
                    <Avatar sx={{ bgcolor: "primary.main" }}>
                      {resp.iniciais}
                    </Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            )}
          </Box>
        </Box>

        {/* Sub-tarefas: lista expansível */}
        {subtarefas.length > 0 && (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mt={1}
              onClick={(e) => {
                e.stopPropagation();
                setSubtarefasExpandidas((prev) => !prev);
              }}
              sx={{ cursor: "pointer" }}
            >
              <Typography variant="caption" color="primary" fontWeight="medium">
                Sub-tarefas ({subtarefas.length})
              </Typography>
              <IconButton size="small" sx={{ p: 0.25 }}>
                {subtarefasExpandidas ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <ExpandMore fontSize="small" />
                )}
              </IconButton>
            </Box>

            <Collapse in={subtarefasExpandidas} timeout="auto" unmountOnExit>
              <Divider sx={{ my: 0.5 }} />
              {subtarefas.map((sub) => (
                <Box
                  key={sub.idSubtarefa}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={0.5}
                  py={0.5}
                >
                  {/* Nome / descrição da sub-tarefa */}
                  <Typography
                    variant="caption"
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textDecoration:
                        sub.idTarefaStatus === 4 ? "line-through" : "none",
                      color:
                        sub.idTarefaStatus === 4
                          ? "text.disabled"
                          : "text.primary",
                    }}
                  >
                    {sub.nome}
                  </Typography>

                  {/* Horas + indicador de timer ativo */}
                  <Box display="flex" alignItems="center" gap={0.25}>
                    <AccessTime
                      sx={{
                        fontSize: 12,
                        color: sub.temTimerAtivo ? "#4caf50" : "text.secondary",
                      }}
                    />
                    <Typography
                      variant="caption"
                      color={sub.temTimerAtivo ? "success.main" : "textSecondary"}
                      fontWeight={sub.temTimerAtivo ? "bold" : "normal"}
                    >
                      {formatTempoReal(sub)}
                    </Typography>
                  </Box>

                  {/* Avatares dos responsáveis (nome no hover) */}
                  {sub.responsaveis && sub.responsaveis.length > 0 && (
                    <AvatarGroup
                      max={2}
                      sx={{
                        "& .MuiAvatar-root": {
                          width: 20,
                          height: 20,
                          fontSize: "0.6rem",
                          border: "1px solid white",
                        },
                      }}
                    >
                      {sub.responsaveis.map((resp) => (
                        <Tooltip key={resp.idUsuario} title={resp.nome}>
                          <Avatar sx={{ bgcolor: "primary.main" }}>
                            {resp.iniciais}
                          </Avatar>
                        </Tooltip>
                      ))}
                    </AvatarGroup>
                  )}

                  {/* 3.1 — Play/Stop POR SUBTAREFA (cronometra a subtarefa).
                      8.1.3 — escondido quando o usuário não é responsável da
                      subtarefa e não tem Tarefa.EditarTerceiros. */}
                  {podeCronometrarSubtarefa(sub) &&
                    (sub.temTimerAtivo ? (
                      <Tooltip title="Parar timer da subtarefa">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            onStopTimer?.(undefined, sub.idSubtarefa);
                          }}
                          sx={{ p: 0.25 }}
                        >
                          <Stop sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Iniciar timer da subtarefa">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPlayTimer?.(undefined, sub.idSubtarefa);
                          }}
                          sx={{ p: 0.25 }}
                        >
                          <PlayArrow sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    ))}
                </Box>
              ))}
            </Collapse>
          </>
        )}
      </CardContent>
    </Card>
  );
};
